import * as React from 'react'
import { isValidElement, type CSSProperties, type ReactNode } from 'react'
import rules from './placement-rules.json'
import residue from './style-residue.json'
import twReach from './tailwind-reach.generated.json'

/**
 * A primitive owns its look; a caller may only PLACE it (30.10.2026, David: «no chance of issues
 * individually caused different places based on custom code»).
 *
 * `placement-rules.json` is an ALLOWLIST, read by this guard and by the build gate
 * (`scripts/style-analyzer.mjs`): size, margin, display, grid/flex position and layout, position,
 * order, z. Any other class or style property a caller hands a primitive is appearance, including
 * a class nobody thought to list and any `--token` re-binding.
 *
 * Every `components/ui` primitive passes what it RECEIVES through `placed()`: its `className`
 * (with its rest props, so `style` is checked too), every `*ClassName` prop, its variant function's
 * `className` option, and an `asChild` child (`placedChild`). In tests an appearance class or style
 * throws, so any rendered page that restyles a shared component fails its suite; in dev it is a
 * console error naming the component. In production it is the identity function.
 *
 * There is no opt-out marker at run time: the gate's `style-ok:` excuses only a value it cannot
 * read, never appearance.
 */
const PLACE = rules.classes.map((s) => new RegExp(s))
const NEVER = rules.variantsNeverPlacement.map((s) => new RegExp(s))
/** Variants Tailwind compiles past the element (`*:`, `selection:`) or to its own pseudo-element (`before:`), from its own table. */
const REACH = new Set<string>([...twReach.variants, ...twReach.functionalVariants, ...twReach.pseudoElementVariants])
const PLACE_STYLE = new Set<string>(rules.style)

/** A class split at its top-level colons: variants, then the base (nested brackets stay whole). */
function partsOf(cls: string): string[] {
  const parts: string[] = []
  let depth = 0, cur = ''
  for (const ch of cls.replace(/^!/, '').replace(/!$/, '')) {
    if (ch === '[') depth++
    if (ch === ']') depth--
    if (ch === ':' && depth === 0) { parts.push(cur); cur = '' } else cur += ch
  }
  parts.push(cur)
  return parts
}

/** The class without its variants (`md:hover:bg-x` → `bg-x`), keeping `[prop:value]` whole. */
export function baseOf(cls: string): string {
  const parts = partsOf(cls)
  return parts[parts.length - 1].replace(/^-/, '')
}

/** Placement: the base is on the list and no variant points past the element (a child, a pseudo-element). */
export const isPlacement = (cls: string) => PLACE.some((re) => re.test(baseOf(cls))) && !partsOf(cls).slice(0, -1).some((v) => REACH.has(v) || NEVER.some((re) => re.test(v)))

export function appearanceClasses(className: string | undefined | null): string[] {
  if (!className) return []
  return className.split(/\s+/).filter((c) => c && !isPlacement(c))
}

/** The style properties that are not placement, `--` custom properties included. */
export function appearanceStyle(style: CSSProperties | undefined | null): string[] {
  if (!style || typeof style !== 'object') return []
  return Object.keys(style).filter((k) => !PLACE_STYLE.has(k)).map((k) => `style.${k}`)
}

const MODE = import.meta.env.MODE
const CHECK = MODE === 'test' || import.meta.env.DEV

type Fiber = { type?: unknown; _debugOwner?: Fiber | null }
const INTERNALS = (React as unknown as { __CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE?: { A?: { getOwner?: () => Fiber | null } | null } })
  .__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE

/** A component's name from its fiber type (function, forwardRef, memo). */
export function componentName(type: unknown): string | null {
  const t = type as { displayName?: string; name?: string; render?: { displayName?: string; name?: string }; type?: unknown } | null
  if (!t || typeof t === 'string') return null
  return t.displayName || t.name || t.render?.displayName || t.render?.name || (t.type ? componentName(t.type) : null) || null
}

/**
 * The component that created the element being rendered: React's `_debugOwner` of the current
 * fiber, development builds only. (The owner STACK is not used: React stops capturing it after
 * 10 000 elements, so long test runs saw an empty stack.) Unknown means no exemption: loud, not silent.
 */
function ownerName(): string | null {
  return componentName(INTERNALS?.A?.getOwner?.()?._debugOwner?.type)
}

/** Per help-pinned file, the frozen (owner, component, class) triples, generated by the gate. */
const RESIDUE = new Set<string>(Object.values(residue.files as unknown as Record<string, string[]>).flat())
export function isFrozen(owner: string | null, component: string, bad: string[]): boolean {
  return !!owner && bad.every((c) => RESIDUE.has(`${owner} ${component} ${c}`))
}

function report(component: string, bad: string[]) {
  const message = `${component} was styled from outside: «${bad.join(' ')}». Give ${component} a named prop for this look in components/ui; callers may only place it (size, margin, display, grid/flex position, order, z).`
  if (isFrozen(ownerName(), component, bad)) return
  if (MODE === 'test') throw new Error(message)
  console.error(message)
}

/**
 * Returns `className` unchanged. In tests and dev, refuses appearance from a caller: in the
 * classes, and in `props.style` when the primitive passes its rest props.
 */
export function placed(className: string | undefined, component: string, props?: object): string | undefined {
  if (!CHECK) return className
  const bad = [...appearanceClasses(className), ...appearanceStyle((props as { style?: CSSProperties } | undefined)?.style)]
  if (bad.length) report(component, bad)
  return className
}

/** For an `asChild` host that merges its look into the child: the child's own classes and style are a caller's. */
export function placedChild(children: ReactNode, component: string): void {
  if (!CHECK || !isValidElement<{ className?: string; style?: CSSProperties }>(children)) return
  const bad = [...appearanceClasses(children.props.className), ...appearanceStyle(children.props.style)]
  if (bad.length) report(`${component} (asChild child)`, bad)
}

/** Wraps a primitive's variant function, so its `className` option is checked like a prop. */
export function placedVariants<F extends (...args: never[]) => string>(fn: F, component: string): F {
  return ((...args: Parameters<F>) => {
    const options = args[0] as { className?: unknown; class?: unknown } | undefined
    if (CHECK && options) for (const v of [options.className, options.class]) if (typeof v === 'string') placed(v, component)
    return fn(...args)
  }) as F
}
